import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { buscarDireccion, puntoDeFiar } from '@/lib/geocodificacion/google'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { OrdenSinUbicar, OrdenUbicada } from '@/lib/supabase/tipos-bd'

/**
 * Ubicar las órdenes en el mapa.
 *
 * Se hace a mano y no solo: mandar direcciones de clientes a un servicio
 * externo es algo que alguien decide y ve pasar, no algo que ocurra de fondo
 * sin que nadie se entere.
 */

export function useOrdenesUbicadas() {
  const consulta = useQuery({
    queryKey: ['ordenes-ubicadas'],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OrdenUbicada[]> => {
      const { data, error } = await supabase.from('vista_ordenes_ubicadas').select('*')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as OrdenUbicada[]
    },
  })

  return { ordenes: consulta.data ?? [] }
}

export function useOrdenesSinUbicar() {
  const consulta = useQuery({
    queryKey: ['ordenes-sin-ubicar'],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OrdenSinUbicar[]> => {
      const { data, error } = await supabase.from('vista_ordenes_sin_ubicar').select('*')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as OrdenSinUbicar[]
    },
  })

  return { pendientes: consulta.data ?? [] }
}

export type Avance = {
  trabajando: boolean
  hechas: number
  total: number
  encontradas: number
  /** Cuál se está buscando ahora, para que se vea que no está colgado. */
  actual: string | null
  error: string | null
}

const QUIETO: Avance = {
  trabajando: false,
  hechas: 0,
  total: 0,
  encontradas: 0,
  actual: null,
  error: null,
}

export function useUbicarPendientes() {
  const clienteConsultas = useQueryClient()
  const [avance, setAvance] = useState<Avance>(QUIETO)

  async function ubicar(pendientes: OrdenSinUbicar[]) {
    if (pendientes.length === 0) return

    setAvance({ ...QUIETO, trabajando: true, total: pendientes.length })

    let hechas = 0
    let encontradas = 0

    for (const orden of pendientes) {
      setAvance((previo) => ({ ...previo, actual: orden.numero, hechas }))

      try {
        const donde = {
          direccion: orden.direccion,
          ciudad: orden.ciudad,
          departamento: orden.departamento,
        }
        const punto = await buscarDireccion(donde)

        // UN PUNTO EQUIVOCADO ES PEOR QUE NINGÚN PUNTO. Sin punto el mapa lo
        // dice y alguien lo ubica a mano; con uno falso el mapa se ve completo
        // y el técnico sale hacia el sitio que no es.
        const veredicto = punto ? puntoDeFiar(punto, donde) : null
        const sirve = veredicto?.sirve === true

        const { error } = await supabase.rpc('fn_ubicar_orden', {
          p_orden_id: orden.id,
          p_latitud: sirve ? punto!.latitud : null,
          p_longitud: sirve ? punto!.longitud : null,
          p_origen: 'geocodificada',
          p_precision: punto?.precision ?? null,
          // Cuando no sirve se guarda POR QUÉ, no el sitio equivocado: quien
          // lo ubique a mano después necesita saber qué pasó.
          p_texto: sirve
            ? punto!.texto
            : veredicto && !veredicto.sirve
              ? veredicto.motivo
              : 'No se encontró la dirección.',
        })

        if (error) throw new Error(mensajeDeError(error))
        if (sirve) encontradas += 1
      } catch (fallo) {
        // Se para en seco: si Google está pidiendo esperar o rechazando la
        // llave, seguir pidiendo solo gasta consultas cobradas.
        setAvance((previo) => ({
          ...previo,
          trabajando: false,
          error: fallo instanceof Error ? fallo.message : 'Falló la búsqueda.',
        }))
        break
      }

      hechas += 1
      setAvance((previo) => ({ ...previo, hechas, encontradas }))
    }

    setAvance((previo) => ({ ...previo, trabajando: false, actual: null, hechas, encontradas }))

    await Promise.all([
      clienteConsultas.invalidateQueries({ queryKey: ['ordenes-ubicadas'] }),
      clienteConsultas.invalidateQueries({ queryKey: ['ordenes-sin-ubicar'] }),
      // Y el mapa de ÓRDENES, que bebe de otra consulta. Faltaba: se ubicaban
      // las veintitrés y ese mapa seguía en blanco hasta recargar la página,
      // que es exactamente la forma de que alguien crea que no funcionó.
      clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] }),
    ])
  }

  return { avance, ubicar }
}

/**
 * PONER EL PUNTO A MANO, ARRASTRÁNDOLO EN EL MAPA.
 *
 * El geocodificador acierta con una dirección urbana bien escrita; con «finca
 * La Esmeralda, vía Pereira» no tiene con qué. Para eso —y para corregir las
 * que importamos con la coordenada movida— hace falta que alguien que conoce el
 * sitio arrastre el alfiler y lo deje donde de verdad es.
 *
 * Se marca como 'manual' a propósito: es la mejor fuente que hay: no la vuelve
 * a pisar el geocodificador, y en el mapa se distingue de las adivinadas.
 */
export function useGuardarUbicacionManual() {
  const clienteConsultas = useQueryClient()
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function guardar(ordenId: string, latitud: number, longitud: number): Promise<boolean> {
    setGuardando(true)
    setError(null)
    try {
      const { error: fallo } = await supabase.rpc('fn_ubicar_orden', {
        p_orden_id: ordenId,
        p_latitud: latitud,
        p_longitud: longitud,
        p_origen: 'manual',
        p_precision: 'exacta',
        p_texto: 'Ubicación puesta a mano en el mapa.',
      })

      if (fallo) throw new Error(mensajeDeError(fallo))

      await Promise.all([
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-ubicadas'] }),
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-sin-ubicar'] }),
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] }),
      ])

      return true
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo guardar la ubicación.')
      return false
    } finally {
      setGuardando(false)
    }
  }

  return { guardar, guardando, error }
}

/**
 * UBICAR UNA SOLA, LA QUE SE ACABA DE CREAR.
 *
 * Por qué esto cambia de criterio respecto al ubicador masivo, que se hace a
 * mano a propósito: allí son decenas de direcciones de golpe y quien pulsa no
 * las escribió. Aquí es UNA, la acaba de teclear la persona que está mirando
 * la pantalla, y la ve ubicarse. Sigue habiendo alguien que decide y que lo ve
 * pasar —que era el punto—, solo que no hace falta un segundo viaje a otra
 * pantalla para completar la orden que acaba de crear.
 *
 * NO REVIENTA LA CREACIÓN SI FALLA. La orden ya existe y es válida sin punto;
 * que Google no encuentre «Chalet Los Naranjos» no puede deshacer un documento
 * del negocio. Se deja sin ubicar y el mapa ofrecerá ubicarla después.
 */
export function useUbicarUna() {
  const clienteConsultas = useQueryClient()

  return async function ubicarUna(ordenId: string): Promise<boolean> {
    try {
      // Se lee de la MISMA vista que usa el ubicador masivo: ahí ya están
      // resueltos la dirección normalizada, la ciudad de la zona y el valor
      // por defecto de Cartago. Repetir esa lógica aquí sería tener dos
      // verdades sobre dónde queda una orden.
      const { data, error } = await supabase
        .from('vista_ordenes_sin_ubicar')
        .select('*')
        .eq('id', ordenId)
        .maybeSingle()

      if (error || !data) return false

      const pendiente = data as unknown as OrdenSinUbicar
      const donde = {
        direccion: pendiente.direccion,
        ciudad: pendiente.ciudad,
        departamento: pendiente.departamento,
      }
      const punto = await buscarDireccion(donde)

      const veredicto = punto ? puntoDeFiar(punto, donde) : null
      const sirve = veredicto?.sirve === true

      await supabase.rpc('fn_ubicar_orden', {
        p_orden_id: ordenId,
        p_latitud: sirve ? punto!.latitud : null,
        p_longitud: sirve ? punto!.longitud : null,
        p_origen: 'geocodificada',
        p_precision: punto?.precision ?? null,
        p_texto: sirve
          ? punto!.texto
          : veredicto && !veredicto.sirve
            ? veredicto.motivo
            : 'No se encontró la dirección.',
      })

      await Promise.all([
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-ubicadas'] }),
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-sin-ubicar'] }),
        clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] }),
      ])

      return sirve
    } catch {
      return false
    }
  }
}
